<template>
  <el-dialog 
    title="项目质保详情" 
    :visible="visible"
    width="1600px"
    :close-on-click-modal="false"
    @close="closeDialog()"
    center
  >
    <div class="warranty-dialog-header">
      <div class="customer-info">
        <div class="customer-name">
          <el-avatar :size="50" :src="defaultAvatar" /> 
          <div>
            <h3>{{ customer.name }}</h3>
            <p>{{ customer.phone }}</p>
          </div>
        </div>
        <div class="warranty-actions">
          <el-button type="primary" icon="el-icon-plus" @click="addWarranty">新增项目</el-button>
        </div>
      </div>
    </div>
    
    <el-table 
      :data="warrantyData" 
      stripe
      border
      style="width: 100%"
      height="500px"
      class="warranty-table"
    >
      <el-table-column prop="cardNumber" label="质保卡编号" width="150" align="center" />
      <el-table-column prop="projectName" label="项目名称" width="150" align="center" />
      <el-table-column prop="projectAddress" label="项目地址" width="250" align="center" />
      <el-table-column prop="storeName" label="质保门店" width="180" align="center">
        <template slot="header">
          <div class="flex-center">
            <i class="el-icon-shop"></i>
            <span>质保门店</span>
          </div>
        </template>
      </el-table-column>

      <el-table-column prop="storeAddress" label="质保门店地址" width="250" align="center">
        <template slot="header">
          <div class="flex-center">
            <i class="el-icon-location-information"></i>
            <span>门店地址</span>
          </div>
        </template>
      </el-table-column>
      
      <el-table-column label="质保内容" width="240" align="center">
        <template slot-scope="scope">
          <div class="warranty-list">
            <!-- 显示可见的质保卡片 -->
            <div 
              v-for="(item, index) in getDisplayedWarranties(scope.row)" 
              :key="index" 
              class="warranty-card"
            >
              <div class="warranty-header">
                <span class="warranty-index">#{{ index + 1 }}</span>
                <el-tag size="mini" type="success">质保内容</el-tag>
              </div>
              <div class="warranty-content">
                <el-tooltip :content="item.content" placement="top">
                  <p>{{ truncateText(item.content, 25) }}</p>
                </el-tooltip>
              </div>
              <div class="warranty-period">
                <span><i class="el-icon-time"></i> {{ item.starTime }}</span> 
                <span><i class="el-icon-time"></i> {{ item.endTime }}</span>
              </div>
            </div>
            
            <!-- 展开/收起按钮 -->
            <div 
              class="toggle-warranties" 
              v-if="scope.row.warranties.length > 1"
            >
              <el-button 
                type="text" 
                @click="toggleWarranties(scope.row)"
                :icon="scope.row.isExpanded ? 'el-icon-caret-top' : 'el-icon-caret-bottom'"
              >
                <template v-if="scope.row.isExpanded">
                  收起
                </template>
                <template v-else>
                  展开其余{{ scope.row.warranties.length - 1 }}项
                </template>
              </el-button>
            </div>
          </div>
        </template>
      </el-table-column>
      
      <!-- 效果图列 -->
      <el-table-column label="效果图" width="300" align="center">
        <template slot-scope="scope">
          <div class="effect-images-container">
            <template v-if="scope.row.effectImages && scope.row.effectImages.length > 0">
              <el-image 
                v-for="(image, index) in scope.row.effectImages" 
                :key="index"
                style="width: 60px; height: 40px; margin-right: 5px;"
                :src="image.url" 
                fit="cover"
                :preview-src-list="prelistImageEffects(scope.row.effectImages)"
              ></el-image>
            </template>
            <div v-else class="no-image">
              <i class="el-icon-picture-outline"></i>
              <div>暂无效果图</div>
            </div>
          </div>
        </template>
      </el-table-column>
      
      <!-- 合同列 - 增加预览按钮 -->
      <el-table-column label="合同" width="300" align="center">
        <template slot="header">
          <div class="flex-center">
            <i class="el-icon-document"></i>
            <span>合同</span>
          </div>
        </template>
        <template slot-scope="scope">
          <div class="contract-actions">
            <el-button 
              type="primary" 
              icon="el-icon-view"
              size="small"
              @click="previewContract(scope.row)"
              :disabled="!scope.row.contract"
            >
              预览
            </el-button>
            <el-button 
              type="success" 
              icon="el-icon-download"
              size="small"
              @click="downloadContract(scope.row)"
              :disabled="!scope.row.contract"
            >
              下载
            </el-button>
          </div>
        </template>
      </el-table-column>
      
      <!-- 操作列 -->
      <el-table-column label="操作" width="160" align="center">
        <template slot-scope="scope">
          <el-button 
            type="text" 
            icon="el-icon-edit"
            @click="editWarrantyItem(scope.row)"
          ></el-button>
          <el-button 
            type="text" 
            icon="el-icon-delete"
            @click="deleteWarrantyItem(scope.row)"
            style="color: #F56C6C;"
          ></el-button>
        </template>
      </el-table-column>
    </el-table>
    
    <div slot="footer" class="dialog-footer">
      <el-button type="primary" @click="closeDialog()">关闭</el-button>
    </div>

    <!-- 质保项目编辑对话框 -->
    <WarrantyItemDialog 
      :visible.sync="warrantyItemDialogVisible"
      :mode="warrantyDialogMode"
      :form="warrantyItemForm"
      @submit="saveWarrantyItem"
    />

    <!-- 合同预览对话框 -->
    <ContractPreviewDialog 
      :visible.sync="previewDialogVisible"
      :type="previewType"
      :url="previewUrl"
      @download="downloadContract"
    />
  </el-dialog>
</template>

<script>
import { createCard, updateCard, delCard } from '../api/admin'
import WarrantyItemDialog from './WarrantyItemDialog'
import ContractPreviewDialog from './ContractPreviewDialog'
import defaultAvatar from '@/assets/default_user.png'

export default {
  name: 'WarrantyDetailsDialog',
  components: {
    WarrantyItemDialog,
    ContractPreviewDialog
  },
  props: {
    visible: Boolean,
    customer: Object
  },
  data() {
    return {
      defaultAvatar,
      warrantyData: [],
      warrantyItemDialogVisible: false,
      warrantyDialogMode: 'add',
      warrantyItemForm: {
        id: null,
        projectName: '',
        projectAddress: '',
        storeName: '',
        storeAddress: '',
        warranties: [
          { 
            content: '', 
            starTime: '',
            endTime: ''
          }
        ],
        effectImages: [],
        contract: '',
        effectImageFile: null,
        contractFile: null,
      },
      previewDialogVisible: false,
      previewType: '',
      previewUrl: '',
    }
  },
  watch: {
    customer: {
      immediate: true,
      handler(newVal) {
        if (newVal && newVal.cards) {
          this.warrantyData = newVal.cards
        } else {
          this.warrantyData = []
        }
      }
    }
  },
  methods: {
    closeDialog() {
      this.$emit('update:visible', false);
    },
    // 文本截断方法
    truncateText(text, maxLength) {
      if (!text) return '';
      if (text.length <= maxLength) return text;
      return text.substring(0, maxLength) + '...';
    },
    
    // 获取要显示的质保内容数组（避免 v-for 和 v-if 混用）
    getDisplayedWarranties(row) {
      if (row.isExpanded) {
        return row.warranties;
      } else {
        // 未展开时只返回第一个元素
        return row.warranties.length > 0 ? [row.warranties[0]] : [];
      }
    },
    
    // 切换质保内容的显示
    toggleWarranties(row) {
      this.$set(row, 'isExpanded', !row.isExpanded);
    },
    
    // 打开编辑质保项目对话框
    editWarrantyItem(row) {
      this.warrantyDialogMode = 'edit';
      // 深拷贝数据，避免直接修改原始数据
      const item = JSON.parse(JSON.stringify(row));
      
      // 转换日期格式
      const warranties = item.warranties.map(w => {
        return {
          content: w.content,
          starTime: w.starTime,
          endTime: w.endTime
        };
      });
      
      this.warrantyItemForm = {
        id: item.projectId,
        projectName: item.projectName,
        projectAddress: item.projectAddress,
        storeName: item.storeName,
        storeAddress: item.storeAddress,
        warranties: warranties,
        effectImages: item.effectImages,
        contract: item.contract,
        orginContract: item.contract
      };
      
      this.warrantyItemDialogVisible = true;
    },
    
    // 新增质保项目（打开对话框）
    addWarranty() {
      this.warrantyDialogMode = 'add';
      this.warrantyItemForm = {
        id: null,
        projectName: '',
        projectAddress: '',
        storeName: '',
        storeAddress: '',
        warranties: [
          { 
            content: '', 
            starTime: '',
            endTime: ''
          }
        ],
        effectImages: [],
        contract: '',
        effectImageFile: null,
        contractFile: null,
      };
      this.warrantyItemDialogVisible = true;
    },
    
    // 删除质保项目
    deleteWarrantyItem(row) {
      this.$confirm(`确定要删除项目 "${row.projectName}" 吗？`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        delCard(row.projectId).then(res => {
          if(res.code == 1) {
            this.$emit('refresh');
            this.$message.success('项目已删除');
          }
        })
      }).catch(() => {
        this.$message.info('操作已取消');
      });
    },
    saveWarrantyItem() {
      const warranties = this.warrantyItemForm.warranties.map(w => {
        if (w.content.trim() && w.starTime && w.endTime) {
          return {
            content: w.content.trim(),
            starTime: w.starTime || '',
            endTime:  w.endTime || ''
          };
        }
      }).filter(Boolean);

      if (warranties.length === 0) {
        this.$message.error('至少需要填写一个有效的质保内容');
        return;
      }

      const formData = new FormData();
      formData.append('customerId', this.customer.id);
      formData.append('name', this.warrantyItemForm.projectName);
      formData.append('address', this.warrantyItemForm.projectAddress);
      formData.append('warrantyShop', this.warrantyItemForm.storeName);      // 新增
      formData.append('warrantyShopAddress', this.warrantyItemForm.storeAddress); // 新增
      formData.append('content', JSON.stringify(warranties));
      
      // 添加多张图片
      this.warrantyItemForm.effectImages.forEach((img, index) => {
        if(img.file != null)
        {
          formData.append(`images`, img.file);
        }else{
          if(img.url != null)
          {
            formData.append("originalImages", img.originUrl)
          }
        }
      });
      
      if(this.warrantyItemForm.contractFile) {
        formData.append('file', this.warrantyItemForm.contractFile);
      }else{
        formData.append('originalFile', this.warrantyItemForm.orginContract)
      }
    
      if (this.warrantyDialogMode === 'add') {
        createCard(formData).then(res => {
          if (res.code == 1) {
            this.$message.success('新增项目成功');
            this.$emit('refresh'); // 通知父组件刷新数据
            this.warrantyItemDialogVisible =false
          }
        }).catch(err => {
          this.$message.error(err.error);
        });
      } else {
        updateCard(this.warrantyItemForm.id, formData).then(res => {
          if (res.code == 1) {
            this.$message.success('项目更新成功');
            this.$emit('refresh'); // 通知父组件刷新数据
            this.warrantyItemDialogVisible =false
          }
        }).catch(err => {
          this.$message.error(err.error);
        });
      }
        
    },

    // 预览合同
    previewContract(row) {
      if (!row.contract) {
        this.$message.warning('该客户没有上传合同文件');
        return;
      }
      
      const fileUrl = row.contract;
      const fileExt = fileUrl.split('.').pop().toLowerCase();
      
      if (fileExt === 'pdf') {
        this.previewType = 'pdf';
      } else if (fileExt === 'docx' || fileExt === 'doc') {
        this.previewType = 'docx';
      } else {
        this.$message.warning('不支持的文件格式，仅支持PDF和Word文档');
        return;
      }
      
      this.previewUrl = fileUrl;
      this.previewDialogVisible = true;
    },
    
    // 下载合同
    downloadContract(row) {
      if (!row.contract) {
        this.$message.warning('该客户没有上传合同文件');
        return;
      }
      
      const link = document.createElement('a');
      link.href = row.contract;
      link.setAttribute('download', `${row.projectName}_合同.${row.contract.split('.').pop()}`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      this.$message.success('开始下载合同文件');
    },
    
    prelistImageEffects(effects) {
      let ret = []
      effects.forEach(item => {ret.push(item.url)})
      return ret;
    }
  }
}
</script>

<style scoped>
.warranty-dialog-header {
  padding: 15px 20px;
  background-color: #f5f7fa;
  border-radius: 4px;
  margin-bottom: 20px;
}

.customer-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.customer-name {
  display: flex;
  align-items: center;
  gap: 15px;
}

.customer-name h3 {
  margin: 0;
  font-size: 18px;
}

.customer-name p {
  margin: 5px 0 0;
  color: #909399;
}

.warranty-table {
  margin-top: 10px;
}

.warranty-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.warranty-card {
  border: 1px solid #eaeaea;
  border-radius: 6px;
  padding: 10px;
  background-color: #f9f9f9;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
}

.warranty-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.warranty-index {
  font-weight: bold;
  color: #409eff;
}

.warranty-content p {
  margin: 0;
  line-height: 1.4;
  font-size: 14px;
}

.warranty-period {
  display: flex;
  justify-content: space-between;
  margin-top: 8px;
  font-size: 12px;
  color: #666;
}

.warranty-period i {
  margin-right: 4px;
}

/* 合同操作按钮样式 */
.contract-actions {
  display: flex;
  gap: 8px;
  justify-content: center;
}

/* 对话框操作按钮样式 */
.warranty-actions {
  display: flex;
  gap: 12px;
  margin-left: auto;
}

.effect-images-container {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  align-items: center;
}

.no-image {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #999;
}

.no-image i {
  font-size: 24px;
  margin-bottom: 5px;
}
</style>